import React, { useEffect, useRef, useState } from "react"
import { webLoadingFalse, webLoadingTrue, webNavigate } from "../../services/navigate.service"
import PlusGreenIcon from "../../images/PlusGreenIcon";
import { validateNoSpecialChars } from "../../validation/ValidationInput";
import { createReason, getDetailReasons } from "../../services/reason.service";
import UpIcon from "../../images/UpIcon";
import DownIcon from "../../images/DownIcon";
import PopupLocation from "../../components/PopupLocation";
import { showToast } from "../../services/toast.service";
import { showApiError } from "../../services/apiError";
import { inventoryFormLimits, limitProps, useFieldLimits, validateAgainstLimits } from "../../services/fieldLimits";
import FieldCounter from "../../components/FieldCounter";
import BackIcon from "../../images/BackIcon";
import RedDeleteIconV2 from "../../images/RedDeleteIconV2";
import CloseIcon from "../../images/CloseIcon";
import { useResponsive } from "../../hook/useResponsive";
import { useDialogFocus, useFormKeyboard } from "../../hook/useFormKeyboard";
const reasonsType: { [key: number]: string } = {
    1: "Corrections",
    2: "Counts",
    3: "Transfers",
    4: "Refunds",
    5: "Voids",
    6: "Sale Cancellation",
};

interface ReasonDetail {
    companyId: string,
    reasonType: number,
    reasonName: string,
    reasonDescription: string,
    impactType: number,
    updatedAt: Date,
    isActive: boolean,
    isDeleted: boolean
}

export interface ReasonsResponse {
    data: {
        data: ReasonDetail[],
        status: 200,
    }
}

interface Type {
    name: string,
    id: number,
    value: number
}

const ReasonDetail: React.FC = () => {

    const numberOfKeys = [
        "Name", "Description", "Type", "Last Update", "Status"
    ]
    const preLanguage = localStorage.getItem("language");
    const [openWarningPopup, setOpenWarningPopup] = useState(false);
    const dropdownRefs = useRef<(HTMLDivElement | null)[]>([]);
    const [index, setIndex] = useState<number>(-1);
    const [openDropdownIndexes, setOpenDropdownIndexes] = useState<{ [key: number]: boolean }>({});
    const [errors, setErrors] = useState<string>('');
    const [showDelete, setShowDelete] = useState<boolean>(false);
    const deleteDialogRef = useRef<HTMLDivElement>(null);
    const [listType, setListType] = useState<Type[]>([
        {
            id: 1,
            name: "Negative",
            value: 0,
        },
        {
            id: 2,
            name: "Positive",
            value: 1,
        },
    ]);
    const { isMobile, getWidth } = useResponsive();
    const kb = useFormKeyboard();
    useDialogFocus(showDelete, deleteDialogRef, () => setShowDelete(false));
    const [typeHighlight, setTypeHighlight] = useState({ row: -1, option: -1 });
    const [language, setLanguage] = useState<any>(null);
    const limits = useFieldLimits(inventoryFormLimits("reason"));
    const path = window.location.pathname.split('/');
    const companyId: string = path[path.length - 2] || "";
    const reasonType: string = path[path.length - 1] || "";
    const [rows, setRows] = useState<ReasonDetail[]>([])
    useEffect(() => {
        if (window.languageContext) {
            setLanguage(window.languageContext.language);
        }
    }, [window, window.languageContext]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            dropdownRefs.current.forEach((ref, idx) => {
                if (ref && !ref.contains(event.target as Node)) {
                    setOpenDropdownIndexes((prev) => ({ ...prev, [idx]: false }));
                }
            });
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        fetchData();
        const handleNewToken = async () => {
            await fetchData();
        };
        document.addEventListener("newToken", handleNewToken);
        return () => {
            document.removeEventListener("newToken", handleNewToken);
        };
    }, []);

    const fetchData = async () => {
        webLoadingTrue();
        try {
            const response: ReasonsResponse = await getDetailReasons(companyId, reasonType);
            if (response.data.status === 200) {
                setRows(response.data.data);
            }
        } catch (error) {
            console.error("Error loading terms:", error);
        } finally {
            webLoadingFalse();
        }
    };

    const addRow = () => {
        const isPreviousValid = rows.length > 0 &&
            rows[rows.length - 1] &&
            rows[rows.length - 1].reasonType >= 0 &&
            rows[rows.length - 1].reasonName !== "" &&
            rows[rows.length - 1].impactType >= 0
        let newRow: ReasonDetail = {
            companyId: companyId,
            reasonType: +reasonType,
            reasonName: "",
            reasonDescription: "",
            impactType: 0,
            isActive: true,
            isDeleted: false,
            updatedAt: new Date()
        };
        if ((isPreviousValid || rows.length === 0)) {
            setRows(prevRows => [...prevRows, newRow]);
        } else {
            setOpenWarningPopup(true);
        }
    }
    const handleClosePopup = () => {
        setOpenWarningPopup(false);
    };
    const handleOpenPopup = () => {
        setOpenWarningPopup(true);
    };

    const formatDate = (dateString: Date) => {
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}.${month}.${year}`;
    }

    const handleInputServiceChange = (e: React.ChangeEvent<HTMLInputElement>, index: number, field: keyof ReasonDetail) => {
        const value = e.target.value;
        setRows((prevRows) => {
            const updatedRows = [...prevRows];
            const row = { ...updatedRows[index] };
            if (field === "impactType") {
                (row[field] as number) = Number(value);
            } else {
                (row[field] as string) = value;
            }
            updatedRows[index] = row;
            return updatedRows;
        });
    };

    const handleToggle = (index: number) => {
        setRows((prevRows) => {
            const updatedRows = [...prevRows];
            const row = { ...updatedRows[index] };
            row.isActive = !row.isActive
            updatedRows[index] = row;
            return updatedRows;
        });
    }

    const handleTypeSelect = (type: Type, index: number) => {
        const updatedRows = [...rows];
        updatedRows[index] = {
            ...updatedRows[index],
            impactType: type?.value,
        };
        setRows(updatedRows);
        setOpenDropdownIndexes((prev) => ({ ...prev, [index]: false }));
        setTypeHighlight({ row: -1, option: -1 });
    };

    const handleTypeDropdownToggle = (index: number) => {
        setOpenDropdownIndexes((prev) => ({
            ...prev,
            [index]: !prev[index],
        }));
    };

    const handleTypeKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, index: number) => {
        const isOpen = !!openDropdownIndexes[index];
        const current = typeHighlight.row === index ? typeHighlight.option : -1;
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            if (!isOpen) setOpenDropdownIndexes((prev) => ({ ...prev, [index]: true }));
            const next = e.key === "ArrowDown" ? Math.min(listType.length - 1, current + 1) : Math.max(0, current - 1);
            setTypeHighlight({ row: index, option: next });
        } else if ((e.key === "Enter" && !e.ctrlKey && !e.metaKey) || e.key === " ") {
            // Ctrl/Cmd+Enter is never swallowed here.
            e.preventDefault();
            if (isOpen && current !== -1) {
                handleTypeSelect(listType[current], index);
            } else {
                handleTypeDropdownToggle(index);
                setTypeHighlight({ row: -1, option: -1 });
            }
        } else if (e.key === "Escape" && isOpen) {
            e.preventDefault();
            setOpenDropdownIndexes((prev) => ({ ...prev, [index]: false }));
            setTypeHighlight({ row: -1, option: -1 });
        }
    };

    // Space toggles, as on a native switch; Enter is left to the form.
    const handleToggleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, index: number) => {
        if (e.key !== " " || e.ctrlKey || e.metaKey || e.altKey) return;
        e.preventDefault();
        if (!e.repeat) handleToggle(index);
    };

    const handleSubmit = async (e: React.SyntheticEvent) => {
        e.preventDefault();
        // One message slot for the table: the first row field over its limit, named by its column.
        const columnLabels: Record<string, string> = { reasonName: "Name", reasonDescription: "Description" };
        for (const row of rows.filter((r) => !r.isDeleted)) {
            const found = Object.entries(validateAgainstLimits(row as any, limits, language, Object.keys(columnLabels)))[0];
            if (found) {
                setErrors(`${language?.[columnLabels[found[0]]] ?? columnLabels[found[0]]}: ${found[1]}`);
                return;
            }
        }
        setErrors('');
        webLoadingTrue();
        try {
            const response: any = await createReason(rows, +reasonType);
            if (response) {
                webNavigate("/" + preLanguage + "/reasons");
                webLoadingFalse();
                showToast({
                    message: "Changes saved successfully",
                    type: 'success',
                    duration: 20000,
                });

            }
        } catch (error: any) {
            console.log("🚀 ~ handleSubmit ~ error:", error)
            webLoadingFalse();
            showApiError(error, { language });
        }
    };

    const handleDelete = (index: number) => {
        setIndex(index);
        setShowDelete(true);
    }

    const handleDeleteReason = () => {
        setRows((prev) => {
            const updated = [...prev]
            updated[index] = {
                ...updated[index],
                isDeleted: true
            };
            return updated
        })
        setShowDelete(false);
    }

    return (
        <div {...kb.scopeProps}>
            <div className="bg-white">
                <div className="flex items-center ">
                    <button
                        type="button"
                        aria-label={language?.['Back'] ?? 'Back'}
                        className="w-6 h-6 flex items-center justify-center cursor-pointer"
                        onClick={() => {
                            webNavigate('/' + preLanguage + '/reasons');
                        }}
                    >
                        <BackIcon />
                    </button>
                    <p
                        style={{ fontSize: '14px', fontWeight: '500', lineHeight: '28px', color: '#222525' }}
                        className="font-inter"
                    >
                        {language && language?.['Back'] ? language?.['Back'] : 'Back'}
                    </p>
                </div>
            </div>
            <p
                style={{
                    margin: '20px 0px',
                    fontSize: '20px',
                    fontWeight: '500',
                    lineHeight: '28px',
                    color: '#222525',
                }}
                className="font-inter"
            >
                {language && language?.[`Edit ${reasonsType[+reasonType]}`] ? language?.[`Edit ${reasonsType[+reasonType]}`] : `Edit ${reasonsType[+reasonType]}`}
            </p>
            <div style={{ height: 'calc(100% - 163px)', }}>
                {errors !== "" && <p className="error">{errors}</p>}
                <div className="w-full h-full" style={{ overflow: 'hidden' }}>
                    {!isMobile &&
                        <div className="flex items-center h-10 gap-x-5" style={{ backgroundColor: "#EBF0F0", borderTopLeftRadius: '12px', borderTopRightRadius: '12px', border: '1px solid #EBF0F0', }} >
                            {numberOfKeys.map((val: string) => {
                                return (
                                    <div
                                        key={val}
                                        className="text-[14px] font-medium leading-[18px] font-inter flex items-center h-full"
                                        style={{
                                            color: "#6A7878",
                                            paddingLeft: "10px",
                                            width: `calc((100% - 40px) / ${numberOfKeys.length - 1})`,
                                        }}
                                    >
                                        {language && language?.[val] ? language?.[val] : val}
                                    </div>
                                )
                            })}
                            <div
                                className="text-[14px] font-inter flex items-center cursor-pointer"
                                style={{ width: `40px` }}
                            >
                            </div>
                        </div>
                    }
                    <div className="scroll-container" style={{ height: isMobile ? '100%' : 'calc(100% - 80px)', overflowY: 'scroll', paddingBottom: isMobile ? '16px' : '' }}>
                        {rows && rows.map((row, index) => {
                            if (!row.isDeleted) {
                                return (
                                    <>
                                        {
                                            isMobile ?
                                                <div key={index} className="" style={{ border: "1px solid #EBF0F0", padding: '16px', borderRadius: '12px', marginBottom: index === rows.length - 1 ? '' : '12px' }}>
                                                    <div className="flex items-center justify-between">
                                                        <div className="space-y-1">
                                                            <p style={{ color: '#6A7878' }}>
                                                                {language && language?.['Name']
                                                                    ? language?.['Name']
                                                                    : 'Name'}
                                                            </p>
                                                            <div className="text-[14px] font-inter flex items-center" style={{ color: "#424242", width: `calc((100% - 40px) / ${numberOfKeys.length - 1})`, }}>
                                                                <div style={{ width: "100%" }}>
                                                                    <input
                                                                        className="text-[14px] font-inter placeholder:text-main text-main flex items-center justify-center  outline-none focus:none bg-transparent"
                                                                        style={{ width: "100%", height: "22px", color: "#05292A" }}
                                                                        value={row.reasonName}
                                                                        {...limitProps(limits, "reasonName")}
                                                                        onChange={(e) => handleInputServiceChange(e, index, "reasonName")}
                                                                        onKeyDown={validateNoSpecialChars}
                                                                    />
                                                                    <FieldCounter value={row.reasonName} max={limits.reasonName?.maxLength} />
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            aria-label={`${language?.['Delete'] ?? 'Delete'} ${row.reasonName ?? ''}`.trim()}
                                                            className="text-[14px] font-inter flex items-center cursor-pointer"
                                                            style={{ color: "#05292A", width: `40px` }}
                                                            onClick={() => handleDelete(index)}
                                                        >
                                                            <RedDeleteIconV2 />
                                                        </button>
                                                    </div>
                                                    <div className="text-[14px] font-inter" style={{ color: "#424242", width: `calc((100% - 40px) / ${numberOfKeys.length - 1})`, margin: '12px 0px' }}>
                                                        <p style={{ color: '#6A7878', marginBottom: '4px' }}>
                                                            {language && language?.['Description']
                                                                ? language?.['Description']
                                                                : 'Description'}
                                                        </p>
                                                        <input
                                                            className="text-[14px] font-inter placeholder:text-main text-main flex items-center justify-center  outline-none focus:none bg-transparent"
                                                            style={{ width: "100%", height: "22px", color: "#05292A" }}
                                                            value={row.reasonDescription}
                                                            {...limitProps(limits, "reasonDescription")}
                                                            onChange={(e) => handleInputServiceChange(e, index, "reasonDescription")}
                                                            onKeyDown={validateNoSpecialChars}
                                                        />
                                                        <FieldCounter value={row.reasonDescription} max={limits.reasonDescription?.maxLength} />
                                                    </div>
                                                    <div className="flex items-center">
                                                        <div className="text-[14px] font-inter" style={{ color: "#05292A", width: `calc(50% - 12px)`, }}>
                                                            <p style={{ color: '#6A7878', marginBottom: '4px' }}>
                                                                {language && language?.['Type']
                                                                    ? language?.['Type']
                                                                    : 'Type'}
                                                            </p>
                                                            {+reasonType === 1 ?
                                                                <div onClick={() => handleTypeDropdownToggle(index)} onKeyDown={(e) => handleTypeKeyDown(e, index)} tabIndex={0} role="combobox" aria-label={language?.['Type'] ?? 'Type'} aria-haspopup="listbox" aria-expanded={!!openDropdownIndexes[index]} className="cursor-pointer text-[14px] font-inter flex items-center" style={{ color: "#424242", width: `calc((100% - 40px) / ${numberOfKeys.length - 1})`, }} >
                                                                    <div className="relative " >
                                                                        {openDropdownIndexes[index] && (
                                                                            <div role="listbox" className="absolute z-10 mt-2 bg-white rounded-lg max-h-60 overflow-auto p-1 shadow-extend" style={{ width: "200px", top: '35px' }}>
                                                                                {listType
                                                                                    .map((type, typeIndex) => (
                                                                                        <div
                                                                                            ref={(el) => (dropdownRefs.current[index] = el)}
                                                                                            key={type.id}
                                                                                            role="option"
                                                                                            aria-selected={row.impactType === type.value}
                                                                                            style={typeHighlight.row === index && typeHighlight.option === typeIndex ? { backgroundColor: "#E7F1F1" } : undefined}
                                                                                            className="px-4 py-2 cursor-pointer text-main text-[14px] font-inter"
                                                                                            onClick={(e) => { handleTypeSelect(type, index); e.stopPropagation(); }}
                                                                                        >
                                                                                            {language?.[type.name] ?? type.name}
                                                                                        </div>
                                                                                    ))
                                                                                }
                                                                            </div>
                                                                        )}
                                                                        {row.impactType === 0 ?
                                                                            <div className="flex items-center" style={{ gap: 2, backgroundColor: "#F6E0E0", borderRadius: "40px", padding: "4px 8px" }}>
                                                                                <p className="font-inter" style={{ fontSize: '12px', fontWeight: 500, color: "#BA0E13" }}>{language?.["Negative"] ?? "Negative"}</p>
                                                                                <div className="flex items-center justify-center" style={{ width: '16px', height: '16px' }}>
                                                                                    <DownIcon />
                                                                                </div>
                                                                            </div>
                                                                            :
                                                                            <div className="flex items-center" style={{ gap: 2, backgroundColor: "#E4EFEA", borderRadius: "40px", padding: "4px 8px" }}>
                                                                                <p className="font-inter" style={{ fontSize: '12px', fontWeight: 500, color: "#007C46" }}>{language?.["Positive"] ?? "Positive"}</p>
                                                                                <div className="flex items-center justify-center" style={{ width: '16px', height: '16px' }}>
                                                                                    <UpIcon />
                                                                                </div>
                                                                            </div>
                                                                        }
                                                                    </div>
                                                                </div>
                                                                :
                                                                <div className="cursor-pointer text-[14px] font-inter flex items-center" style={{ color: "#05292A", width: `calc(50% - 12px)` }} >
                                                                    /
                                                                </div>
                                                            }
                                                        </div>
                                                        <div className="text-[14px] font-inter " style={{ color: "#05292A", width: `calc(50% - 12px)`, }}>
                                                            <p style={{ color: '#6A7878', marginBottom: '4px' }}>
                                                                {language && language?.['Last updated']
                                                                    ? language?.['Last updated']
                                                                    : 'Last updated'}
                                                            </p>
                                                            <p className="text-[14px] font-inter flex items-center" style={{ color: '#05292A' }}> {formatDate(row.updatedAt)}</p>
                                                        </div>
                                                    </div>
                                                    <div className="text-[14px] font-inter " style={{ color: "#05292A", width: `100%`, marginTop: '12px' }}>
                                                        <p style={{ color: '#6A7878', marginBottom: '4px' }}>
                                                            {language && language?.['Status']
                                                                ? language?.['Status']
                                                                : 'Status'}
                                                        </p>
                                                        <div className="flex items-center gap-x-3">
                                                            <div className={`switch ${row?.isActive ? 'on' : 'off'}`} role="switch" aria-checked={!!row?.isActive} aria-label={language?.['Status'] ?? 'Status'} tabIndex={0} onKeyDown={(e) => handleToggleKeyDown(e, index)} onClick={() => { handleToggle(index) }}>
                                                                <div className={`shadow_toggle slider ${row?.isActive ? 'move-right' : 'move-left'}`}></div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                :
                                                <div key={index} className="h-10 flex items-center gap-x-5" style={{ border: "1px solid #EBF0F0" }}>
                                                    <div className="text-[14px] font-inter flex items-center" style={{ color: "#424242", paddingLeft: "10px", width: `calc((100% - 40px) / ${numberOfKeys.length - 1})`, }}>
                                                        <div style={{ width: "100%" }}>
                                                            <input
                                                                className="text-[14px] font-inter placeholder:text-main text-main flex items-center justify-center  outline-none focus:none bg-transparent"
                                                                style={{ width: "100%", height: "22px", color: "#05292A" }}
                                                                value={row.reasonName}
                                                                {...limitProps(limits, "reasonName")}
                                                                onChange={(e) => handleInputServiceChange(e, index, "reasonName")}
                                                                onKeyDown={validateNoSpecialChars}
                                                            />
                                                            <FieldCounter value={row.reasonName} max={limits.reasonName?.maxLength} />
                                                        </div>
                                                    </div>
                                                    <div className="text-[14px] font-inter flex items-center" style={{ color: "#424242", paddingLeft: "10px", width: `calc((100% - 40px) / ${numberOfKeys.length - 1})`, }}>
                                                        <div style={{ width: "100%" }}>
                                                            <input
                                                                className="text-[14px] font-inter placeholder:text-main text-main flex items-center justify-center  outline-none focus:none bg-transparent"
                                                                style={{ width: "100%", height: "22px", color: "#05292A" }}
                                                                value={row.reasonDescription}
                                                                {...limitProps(limits, "reasonDescription")}
                                                                onChange={(e) => handleInputServiceChange(e, index, "reasonDescription")}
                                                                onKeyDown={validateNoSpecialChars}
                                                            />
                                                            <FieldCounter value={row.reasonDescription} max={limits.reasonDescription?.maxLength} />
                                                        </div>
                                                    </div>
                                                    {+reasonType === 1 ?
                                                        <div onClick={() => handleTypeDropdownToggle(index)} onKeyDown={(e) => handleTypeKeyDown(e, index)} tabIndex={0} role="combobox" aria-label={language?.['Type'] ?? 'Type'} aria-haspopup="listbox" aria-expanded={!!openDropdownIndexes[index]} className="cursor-pointer text-[14px] font-inter flex items-center" style={{ color: "#424242", paddingLeft: "10px", width: `calc((100% - 40px) / ${numberOfKeys.length - 1})`, }} >
                                                            <div className="relative " >
                                                                {openDropdownIndexes[index] && (
                                                                    <div role="listbox" className="absolute z-10 mt-2 bg-white rounded-lg max-h-60 overflow-auto p-1 shadow-extend" style={{ width: "200px", top: '35px' }}>
                                                                        {listType
                                                                            .map((type, typeIndex) => (
                                                                                <div
                                                                                    ref={(el) => (dropdownRefs.current[index] = el)}
                                                                                    key={type.id}
                                                                                    role="option"
                                                                                    aria-selected={row.impactType === type.value}
                                                                                    style={typeHighlight.row === index && typeHighlight.option === typeIndex ? { backgroundColor: "#E7F1F1" } : undefined}
                                                                                    className="px-4 py-2 cursor-pointer text-main text-[14px] font-inter"
                                                                                    onClick={(e) => { handleTypeSelect(type, index); e.stopPropagation(); }}
                                                                                >
                                                                                    {language?.[type.name] ?? type.name}
                                                                                </div>
                                                                            ))
                                                                        }
                                                                    </div>
                                                                )}
                                                                {row.impactType === 0 ?
                                                                    <div className="flex items-center" style={{ gap: 2, backgroundColor: "#F6E0E0", borderRadius: "40px", padding: "4px 8px" }}>
                                                                        <p className="font-inter" style={{ fontSize: '12px', fontWeight: 500, color: "#BA0E13" }}>{language?.["Negative"] ?? "Negative"}</p>
                                                                        <div className="flex items-center justify-center" style={{ width: '16px', height: '16px' }}>
                                                                            <DownIcon />
                                                                        </div>
                                                                    </div>
                                                                    :
                                                                    <div className="flex items-center" style={{ gap: 2, backgroundColor: "#E4EFEA", borderRadius: "40px", padding: "4px 8px" }}>
                                                                        <p className="font-inter" style={{ fontSize: '12px', fontWeight: 500, color: "#007C46" }}>{language?.["Positive"] ?? "Positive"}</p>
                                                                        <div className="flex items-center justify-center" style={{ width: '16px', height: '16px' }}>
                                                                            <UpIcon />
                                                                        </div>
                                                                    </div>
                                                                }
                                                            </div>
                                                        </div>
                                                        :
                                                        <div className="cursor-pointer text-[14px] font-inter flex items-center" style={{ color: "#05292A", paddingLeft: "10px", width: `calc((100% - 40px) / ${numberOfKeys.length - 1})`, }} >
                                                            /
                                                        </div>
                                                    }
                                                    <div className="text-[14px] font-inter flex items-center" style={{ color: "#05292A", paddingLeft: "10px", width: `calc((100% - 40px) / ${numberOfKeys.length - 1})`, }}>
                                                        <p className="text-[14px] font-inter flex items-center" style={{ color: '#05292A' }}> {formatDate(row.updatedAt)}</p>
                                                    </div>
                                                    <div className="text-[14px] font-inter flex items-center" style={{ color: "#05292A", paddingLeft: "10px", width: `calc((100% - 40px) / ${numberOfKeys.length - 1})`, }}>
                                                        <div className="flex items-center gap-x-3">
                                                            <div className={`switch ${row?.isActive ? 'on' : 'off'}`} role="switch" aria-checked={!!row?.isActive} aria-label={language?.['Status'] ?? 'Status'} tabIndex={0} onKeyDown={(e) => handleToggleKeyDown(e, index)} onClick={() => { handleToggle(index) }}>
                                                                <div className={`shadow_toggle slider ${row?.isActive ? 'move-right' : 'move-left'}`}></div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        aria-label={`${language?.['Delete'] ?? 'Delete'} ${row.reasonName ?? ''}`.trim()}
                                                        className="text-[14px] font-inter flex items-center cursor-pointer"
                                                        style={{ color: "#05292A", width: `40px` }}
                                                        onClick={() => handleDelete(index)}
                                                    >
                                                        <RedDeleteIconV2 />
                                                    </button>
                                                </div>
                                        }
                                    </>
                                )
                            }
                        })}
                        {
                            !isMobile &&
                            <button
                                type="button"
                                onClick={addRow}
                                style={{ width: '100%', height: '40px', padding: '10px 16px', gap: '6px', borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px', border: '1px solid #EBF0F0', cursor: 'pointer', borderTop: 'none', }}
                                className="flex items-center cursor-pointer"
                            >
                                <div className='flex items-center justify-center' style={{ width: '24px', height: '24px' }}>
                                    <PlusGreenIcon />
                                </div>
                                <p className='font-inter' style={{ fontSize: '14px', fontWeight: '500', color: '#0F7679' }}> {language && language?.['Add reason'] ? language?.['Add reason'] : 'Add reason'}</p>
                            </button>
                        }
                    </div>
                </div>
            </div>
            <div
                className="w-full flex items-center justify-end shadow_button relative"
                style={{ gap: "12px", paddingTop: "24px", }}
            >
                <div className="absolute" style={{ left: "-24px", top: 0, height: "1px", backgroundColor: "#DBE4E4", width: isMobile ? 'calc(100vw + 32px)' : "calc(-256px + 48px + 100vw)" }}></div>
                <button
                    type="button"
                    onClick={() => { webNavigate("/" + preLanguage + '/reasons') }}
                    style={{
                        padding: '10px 24px',
                        borderRadius: '30px',
                        color: '#222525',
                        fontSize: '14px',
                        fontWeight: '500',
                        border: '1px solid #C9D7D8',
                    }}
                    className="font-inter cursor-pointer"
                >
                    {language && language?.['Cancel'] ? language?.['Cancel'] : 'Cancel'}
                </button>
                <button type="button" style={{
                    padding: '10px 24px',
                    borderRadius: '30px',
                    color: '#FFFFFF',
                    fontSize: '14px',
                    fontWeight: '500',
                    backgroundColor: '#0F7679',
                }}
                    className="font-inter cursor-pointer"
                    {...kb.primaryProps(handleSubmit)}
                >
                    <p className="text-[14px] leading-[22px] font-inter">{language && language?.["Save"] ? language?.["Save"] : "Save"}</p>
                </button>
            </div>
            <div
                ref={deleteDialogRef}
                role="dialog"
                aria-modal="true"
                className={`absolute w-full h-full top-0 left-0 flex items-center justify-center px-4 ${showDelete ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
                    } transition-opacity duration-300 ease-in-out`}
                style={{ backgroundColor: "#22252552", zIndex: "200", transition: "opacity 0.3s ease, visibility 0.3s ease", width: "100vw", height: "100vh", left: 0, top: 0, visibility: showDelete ? 'visible' : 'hidden' }}
            >
                <div
                    className={`transform transition-transform duration-300 ease-in-out ${showDelete ? 'translate-y-0' : 'translate-y-4 opacity-0'
                        }`}
                    style={{
                        padding: '24px',
                        borderRadius: '24px',
                        backgroundColor: '#FFFFFF',
                        width: '530px',
                        transition: 'transform 0.3s ease, opacity 0.3s ease',
                    }}
                >
                    <div className="flex items-center justify-between">
                        <p
                            style={{ fontSize: '20px', color: '#222525' }}
                            className="font-semibold font-inter leading-[24px]"
                        >
                            {language && language?.[`Delete ${reasonsType[+reasonType]}`] ? language?.[`Delete ${reasonsType[+reasonType]}`] : `Delete ${reasonsType[+reasonType]}`}

                        </p>
                        <button
                            type="button"
                            aria-label={language?.['Close'] ?? 'Close'}
                            onClick={() => setShowDelete(false)}
                            className="flex items-center justify-center cursor-pointer"
                            style={{
                                width: '24px',
                                height: '24px',
                                borderRadius: '100%',
                            }}
                        >
                            <CloseIcon />
                        </button>
                    </div>
                    <div className="flex items-center justify-between" style={{ marginTop: '12px' }}>
                        <p
                            className="text-[14px] font-inter leading-[24px]"
                            style={{ color: '#6A7878' }}
                        >
                            {language &&
                                language?.[
                                'This correction will be permanently deleted. Are you sure you want to continue?'
                                ]
                                ? language?.[
                                'This correction will be permanently deleted. Are you sure you want to continue?'
                                ]
                                : 'This correction will be permanently deleted. Are you sure you want to continue?'}
                        </p>
                    </div>
                    <div className="flex items-center justify-end" style={{ marginTop: '24px', gap: '12px' }}>
                        <button
                            type="button"
                            onClick={() => setShowDelete(false)}
                            className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
                            style={{
                                borderRadius: '30px',
                                padding: '10px 24px',
                                border: '1px solid #C9D7D8',
                                color: '#222525',
                            }}
                        >
                            {language && language?.['Cancel'] ? language?.['Cancel'] : 'Cancel'}
                        </button>
                        <button
                            type="button"
                            onClick={handleDeleteReason}
                            className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
                            style={{
                                borderRadius: '30px',
                                padding: '10px 24px',
                                backgroundColor: '#BA0E13',
                                color: '#FFFFFF',
                            }}
                        >
                            {language && language?.['Delete'] ? language?.['Delete'] : 'Delete'}
                        </button>
                    </div>
                </div>
            </div>
            <PopupLocation show={openWarningPopup} title="Create Reason" desc="Please complete the reason name to add a new row." onClose={handleClosePopup} onOpen={handleOpenPopup} />
        </div>
    )
}
export default ReasonDetail